import type { GoogleTagManagerEvent } from '@/lib/analytics/google-tag-manager-events';
import type { ImageValue } from './image';

// ============================================
// Design Settings Types
// ============================================

export type ColorSettings = {
  headerBackground: string;
  headerLinks: string;
  siteBackground: string;
  siteText: string;
  siteBackgroundAlt: string;
  siteTextAlt: string;
  primaryButtonBackground: string;
  primaryButtonText: string;
  secondaryButtonBackground: string;
  secondaryButtonText: string;
  cardBackground: string;
  cardText: string;
  borders: string;
  footerBackground: string;
  footerText: string;
  footerLinks: string;
};

export type ButtonStyle = 'solid' | 'outline';
export type ButtonShape = 'sharp' | 'smooth' | 'round';

export interface ButtonSettings {
  style?: ButtonStyle;
  shape?: ButtonShape;
}

export interface FontSettings {
  pairingName?: string;
  headings?: {
    font?: string;
    weight?: string;
    capitalization?: string;
  };
  paragraphs?: {
    font?: string;
    weight?: string;
    size?: string;
  };
}

// ============================================
// Palette & Pairing Types
// ============================================

export type ColorCategory =
  | 'recommended'
  | 'aggressive'
  | 'understanding'
  | 'empathetic'
  | 'proven'
  | 'distinguished'
  | 'innovative';

export type ColorPalette = {
  name: string;
  colors: ColorSettings;
  category?: ColorCategory;
};

export type FontCategory = 'recommended' | 'serifs' | 'sans-serifs' | 'mixed';

export type FontPairing = {
  name: string;
  headingFont: string;
  paragraphFont: string;
  headingFontFamily: string;
  paragraphFontFamily: string;
  headingFontWeight?: string;
  paragraphFontWeight?: string;
  category: FontCategory;
};

// ============================================
// User & Account Types
// ============================================

export type UserRole = 'root' | 'admin' | 'user' | 'billing';

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  avatar?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
}

export type CurrentUser = User;

export interface PendingInvitation {
  id: string;
  invitationId: string;
  email: string;
  role: UserRole;
  name: string;
  avatar?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
}

export type UserAccessTarget = Pick<User, 'id' | 'role'>;

export interface Permissions {
  domainSettings?: boolean;
  promptLogs?: boolean;
}

export type CreditUsageLevel = 'warning' | 'critical' | 'exhausted';

export interface GraceAllocation {
  credits: number;
  remaining: number;
  percentUsed: number;
  expiresAt: string | null;
}

export interface CreditStatus {
  percentUsed: number;
  level: CreditUsageLevel | null;
  /** Null when Grace credits make the account critical before a usage threshold is crossed. */
  threshold: number | null;
  used: number;
  allowance: number;
  remaining: number;
  banked: number;
  periodEndsAt: string | null;
  grace: GraceAllocation | null;
  /** True only when Grace credits are the account's last remaining credit source. */
  graceOnly: boolean;
}

export interface UsageThresholdNotice {
  /** Stable identifier used to avoid duplicating this notice in one thread. */
  noticeId: string;
  kind: 'usage_threshold';
  threshold: number;
  level: CreditUsageLevel;
  percentUsed: number;
  remaining: number;
  crossedAt: string;
  grace: null;
  graceOnly: false;
}

export interface GraceCreditNotice {
  /** Stable identifier used to avoid duplicating this notice in one thread. */
  noticeId: string;
  kind: 'grace_granted';
  threshold: number;
  level: CreditUsageLevel;
  percentUsed: number;
  remaining: number;
  crossedAt: string;
  grace: GraceAllocation;
  graceOnly: boolean;
  periodEndsAt?: string | null;
}

export type CreditNotice = UsageThresholdNotice | GraceCreditNotice;

export interface SharedData {
  currentUser: CurrentUser;
  flash: Flash;
  permissions?: Permissions;
  site?: Site;
  snapshotUrl?: string;
  awsWafIntegrationScriptUrl?: string | null;
  userAccounts?: Account[];
  accountId?: string;
  aiCreditStatus?: CreditStatus | null;
  newReceivedReviewsCount?: number;
  aiServiceUrl?: string;
  accountActive?: boolean;
  [key: string]: unknown;
  pendingSignup?: {
    registrantId: string;
    accountName: string;
    step: string;
  };
  intercom: {
    baseUrl: string;
    appId: string;
  };
  trackingData?: GoogleTagManagerEvent[];
}

export interface Flash {
  alert?: string;
  notice?: string;
  restored?: boolean;
}

// Keep the values lowercase to match the page folder names
export enum LayoutType {
  PUBLIC = 'public',
  CMS = 'cms',
  BLANK = 'blank',
}

export interface SiteLogo {
  text?: string;
  imageUrl?: string;
  width?: number | null;
  height?: number | null;
  assetId?: string;
  assetFolderId?: string;
  source?: 'template' | 'asset' | 'ai';
  templateId?: string;
}

export interface Site {
  id: number;
  subdomain?: string;
  logo?: SiteLogo;
  logoTagline?: string | null;
  contactPhone?: string;
  secondaryContactPhone?: string;
  primaryDomain?: string;
  timeZoneIdentifier?: string;
  colors?: ColorSettings | null;
  fonts?: FontSettings | null;
  buttons?: ButtonSettings | null;
  generatedColorPalettes?: ColorPalette[] | null;
  generatedFontPairings?: FontPairing[] | null;
  seo?: {
    showCanonicalTags?: boolean;
    robotsTxt?: string;
  };
  firmName?: string;
  customScripts?: CustomScripts;
  googleAnalyticsMeasurementId?: string | null;
  legalNotice?: {
    showCookieBanner: boolean;
    cookieBannerText: string;
    universalDisclaimer: string;
  };
  caseResultLayoutCode?: string | null;
  blogLayoutCode?: string | null;
}

export interface CustomScripts {
  jsonStructuredData?: string | null;
  headerScripts?: string | null;
  footerScripts?: string | null;
}

export interface SpecialPageMeta {
  caseResultLayoutCode?: string | null;
  blogLayoutCode?: string | null;
  hasBlogPosts?: boolean;
  hasCaseResults?: boolean;
  hasBlogIndex?: boolean;
  hasCaseResultIndex?: boolean;
}

export interface Account {
  id: number;
  name: string;
  subdomain: string;
  active?: boolean;
}

export interface Profile {
  id?: string;
  name?: string;
  image?: ImageValue | null;
  imageUrl?: string;
  bio?: string;
}

export interface Topic {
  id: string;
  name: string;
  usageCount?: number;
}

export interface PracticeArea {
  id: string;
  name: string;
  usageCount?: number;
}

export interface Court {
  id: string;
  name: string;
}

export interface MetadataFieldLimit {
  maxSelected?: number;
  maxLength?: number;
}

export interface ContentModelMetadataConfig {
  fields: Partial<
    Record<'author' | 'topics' | 'attorney' | 'courts' | 'practiceAreas', MetadataFieldLimit>
  >;
}
